// Admin dashboard pieces: metric cards, the community list, the detail grid,
// and the onboarding wizard. All flat, zero-radius, flush-left — see ui.jsx.

import React, { useRef, useState, useEffect } from "react";
import { api } from "./api.js";
import { Badge, Button, Console, Field, Progress, inputStyle } from "./ui.jsx";
import { WIZARD_STEPS, placeOf } from "./useAdmin.js";
import { UnitsStep, ResidentsStep } from "./admin/DirectorySteps";

const label = { font: "600 10px/1 var(--bq-font-display)", letterSpacing: ".14em", textTransform: "uppercase", color: "var(--muted2)" };
const note = { font: "400 11.5px/1.35 var(--bq-font-body)", color: "var(--muted2)" };

export function MetricRow({ metrics, tone = "primary" }) {
  const muted = tone === "muted";
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: 1,
        background: "var(--rule)",
        border: muted ? "1px solid var(--rule2)" : "1px solid var(--ink)",
        borderTop: muted ? "none" : undefined,
        marginBottom: muted ? 26 : 0,
      }}
    >
      {metrics.map((m) => (
        <div
          key={m.key}
          style={{
            background: muted ? "var(--surface)" : "var(--bg)",
            padding: muted ? "16px 18px 18px" : "18px 18px 20px",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <div style={label}>{m.key}</div>
          <div
            style={{
              font: `800 clamp(${muted ? "24px, 3.2vw, 32px" : "32px, 4.4vw, 44px"})/0.92 var(--bq-font-display)`,
              letterSpacing: "-0.035em",
              color: muted ? "var(--muted2)" : "var(--ink)",
            }}
          >
            {m.value}
          </div>
          <div style={note}>{m.note}</div>
        </div>
      ))}
    </div>
  );
}

export function CommunityFilter({ filter, onFilter, status, onStatus, options, count }) {
  return (
    <>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 10 }}>
        <div style={{ font: "800 clamp(18px, 2.6vw, 22px)/1 var(--bq-font-display)", letterSpacing: "-0.02em" }}>Communities</div>
        <div style={{ font: "400 11px/1 var(--bq-font-display)", color: "var(--muted2)" }}>{count}</div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", borderTop: "2px solid var(--ink)", borderBottom: "1px solid var(--rule)", padding: "12px 0" }}>
        <input
          value={filter}
          onChange={(e) => onFilter(e.target.value)}
          placeholder="Filter by name, city, state or ZIP"
          style={{ ...inputStyle, flex: 1, minWidth: 200, border: "1px solid var(--rule2)", minHeight: 40, font: "400 13px/1 var(--bq-font-body)" }}
        />
        <div style={{ display: "flex", flexWrap: "wrap", border: "1px solid var(--rule2)" }}>
          {options.map((opt) => {
            const active = opt === status;
            return (
              <button
                key={opt}
                onClick={() => !active && onStatus(opt)}
                style={{
                  border: "none",
                  background: active ? "var(--ink)" : "transparent",
                  color: active ? "var(--bg)" : "var(--muted2)",
                  padding: "9px 13px",
                  minHeight: 40,
                  font: "600 10px/1 var(--bq-font-display)",
                  letterSpacing: ".1em",
                  textTransform: "uppercase",
                  cursor: active ? "default" : "pointer",
                }}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

export function CommunityList({ items, selectedId, onSelect }) {
  if (items.length === 0) {
    return (
      <div style={{ padding: "26px 14px", borderBottom: "1px solid var(--rule)", font: "400 13px/1.5 var(--bq-font-body)", color: "var(--muted2)" }}>
        No communities match that filter.
      </div>
    );
  }
  return (
    <div>
      {items.map((c) => {
        const active = c.id === selectedId;
        return (
          <button
            key={c.id}
            onClick={() => onSelect(c.id)}
            style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              gap: 14, flexWrap: "wrap", width: "100%", textAlign: "left",
              border: "none",
              borderBottom: "1px solid var(--rule)",
              borderLeft: `3px solid ${active ? "var(--accent)" : "transparent"}`,
              background: active ? "var(--accentTint)" : "transparent",
              padding: 14, minHeight: 56, cursor: "pointer",
            }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ font: `${active ? 700 : 600} 14px/1.25 var(--bq-font-display)`, color: "var(--ink)" }}>{c.name}</div>
              <div style={{ ...note, marginTop: 3 }}>
                {placeOf(c)} · {c.units} units · {c.docs} docs · {c.resolutions} resolutions
              </div>
            </div>
            <Badge tone={active ? "outline" : "muted"} style={{ border: "none", color: active ? "var(--accentDeep)" : "var(--muted2)", padding: 0 }}>
              {c.status}
            </Badge>
          </button>
        );
      })}
    </div>
  );
}

function DetailCardsGrid({ cards }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 1, background: "var(--rule)", border: "1px solid var(--rule2)" }}>
      {cards.map((card) => (
        <div key={card.key} style={{ background: "var(--bg)", padding: "16px 18px 18px", display: "flex", flexDirection: "column", gap: 7 }}>
          <div style={label}>{card.key}</div>
          <div style={{ font: "800 clamp(20px, 2.6vw, 26px)/1.08 var(--bq-font-display)", letterSpacing: "-0.025em", color: "var(--ink)" }}>{card.value}</div>
          <div style={note}>{card.note}</div>
        </div>
      ))}
    </div>
  );
}

/** Inline expand, kept for callers that want the community's stats folded
 * into the list rather than a full page -- AdminApp.jsx itself now uses
 * CommunityDetailPage (below) instead, reached by clicking a row. */
export function CommunityDetail({ community, cards, onClose }) {
  if (!community) return null;
  return (
    <div style={{ marginTop: 28, borderTop: "2px solid var(--ink)", paddingTop: 16 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 14 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ font: "800 clamp(20px, 3vw, 26px)/1.08 var(--bq-font-display)", letterSpacing: "-0.025em" }}>{community.name}</div>
          <div style={{ font: "400 12px/1.4 var(--bq-font-display)", color: "var(--muted2)", marginTop: 5 }}>
            {placeOf(community)} · {community.status}
          </div>
        </div>
        <Button variant="secondary" onClick={onClose} style={{ border: "1px solid var(--rule2)", color: "var(--muted2)", minHeight: 40, padding: "9px 13px" }}>
          Close
        </Button>
      </div>
      <DetailCardsGrid cards={cards} />
    </div>
  );
}

// ── Chevron used for drill-down list rows ────────────────────────────────────
function ChevronRight() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flex: "none", color: "var(--muted2)" }}>
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

// ── Back breadcrumb for detail panels ────────────────────────────────────────
function DetailBreadcrumb({ label, onBack }) {
  return (
    <button
      onClick={onBack}
      style={{
        display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer",
        padding: "0 0 14px", marginBottom: 14, borderBottom: "1px solid var(--rule)",
        font: "600 10px/1 var(--bq-font-display)", letterSpacing: ".1em", textTransform: "uppercase", color: "var(--muted2)",
      }}
    >
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
      </svg>
      {label}
    </button>
  );
}

// ── Resident detail panel ─────────────────────────────────────────────────────
const STATUS_ORDER = { open: 0, in_progress: 1, resolved: 2, closed: 3 };

const BOARD_SEATS = ["President", "Treasurer", "Secretary", "Member at large"];

function ResidentDetail({ resident, units, communityId, onBack, onResidentUpdated }) {
  const linked = (units || []).filter((u) => (resident.units || []).includes(u.id));
  const [resTickets, setResTickets] = useState(null);
  const [resTicketsErr, setResTicketsErr] = useState(null);
  const [localBoard, setLocalBoard] = useState(!!resident.board);
  const [localRole, setLocalRole] = useState(resident.role || "");
  const [boardSaving, setBoardSaving] = useState(false);
  const [boardErr, setBoardErr] = useState(null);
  useEffect(() => { setLocalBoard(!!resident.board); setLocalRole(resident.role || ""); setBoardErr(null); }, [resident.id]);

  useEffect(() => {
    if (!communityId || !resident.email) return;
    let cancelled = false;
    api.listAdminTickets(communityId, { requesterIdentifier: resident.email })
      .then((body) => {
        if (cancelled) return;
        const sorted = (body.tickets || []).slice().sort((a, b) => {
          const so = (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9);
          if (so !== 0) return so;
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        });
        setResTickets(sorted);
      })
      .catch((err) => {
        if (cancelled) return;
        setResTicketsErr(err?.message || String(err));
        setResTickets([]);
      });
    return () => { cancelled = true; };
  }, [communityId, resident.email]);

  const toggleBoard = async (newBoard) => {
    setBoardSaving(true); setBoardErr(null);
    try {
      const patch = { board: newBoard };
      if (newBoard && !localRole) patch.role = "Member at large";
      const updated = await api.patchResident(communityId, resident.id, patch);
      setLocalBoard(!!updated.board); setLocalRole(updated.role || "");
      onResidentUpdated && onResidentUpdated(updated);
    } catch (err) { setBoardErr(err?.message || String(err)); }
    finally { setBoardSaving(false); }
  };
  const changeSeat = async (seat) => {
    setBoardSaving(true); setBoardErr(null);
    try {
      const updated = await api.patchResident(communityId, resident.id, { role: seat });
      setLocalBoard(!!updated.board); setLocalRole(updated.role || "");
      onResidentUpdated && onResidentUpdated(updated);
    } catch (err) { setBoardErr(err?.message || String(err)); }
    finally { setBoardSaving(false); }
  };

  return (
    <div>
      <DetailBreadcrumb label="Back to residents" onBack={onBack} />
      <div style={{ font: "800 clamp(18px, 2.6vw, 22px)/1.08 var(--bq-font-display)", letterSpacing: "-0.02em", marginBottom: 4 }}>{resident.name}</div>
      <div style={{ ...note, marginBottom: 18 }}>{resident.rel}{localBoard ? ` · ${localRole || "Board member"}` : ""}</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 1, background: "var(--rule)", border: "1px solid var(--rule2)", marginBottom: 20 }}>
        {[
          { k: "Email", v: resident.email || "—" },
          { k: "Phone", v: resident.phone || "—" },
          { k: "Role", v: resident.rel || "—" },
          { k: "Board seat", v: localBoard ? (localRole || "Member at large") : "No" },
        ].map(({ k, v }) => (
          <div key={k} style={{ background: "var(--bg)", padding: "14px 16px 16px", display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={label}>{k}</div>
            <div style={{ font: "600 13px/1.3 var(--bq-font-display)", color: "var(--ink)" }}>{v}</div>
          </div>
        ))}
      </div>
      {/* ── Board assignment ── */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ ...label, marginBottom: 10 }}>Board membership</div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <button
            onClick={() => toggleBoard(!localBoard)}
            disabled={boardSaving}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              border: "1px solid var(--rule2)", background: localBoard ? "var(--ink)" : "transparent",
              padding: "8px 14px", cursor: boardSaving ? "wait" : "pointer",
              font: "600 11px/1 var(--bq-font-display)", letterSpacing: ".08em", textTransform: "uppercase",
              color: localBoard ? "var(--bg)" : "var(--muted2)", transition: "background .15s,color .15s",
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill={localBoard ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
            {localBoard ? "Board member" : "Not on board"}
          </button>
          {localBoard && (
            <select
              value={localRole || "Member at large"}
              onChange={(e) => changeSeat(e.target.value)}
              disabled={boardSaving}
              style={{
                border: "1px solid var(--rule2)", background: "var(--bg)", padding: "8px 10px",
                font: "600 11px/1 var(--bq-font-display)", letterSpacing: ".04em", color: "var(--ink)",
                cursor: boardSaving ? "wait" : "pointer",
              }}
            >
              {BOARD_SEATS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          {boardSaving && <span style={{ font: "400 11px/1 var(--bq-font-body)", color: "var(--muted2)" }}>Saving…</span>}
          {boardErr && <span style={{ font: "400 11px/1 var(--bq-font-body)", color: "var(--accent)" }}>{boardErr}</span>}
        </div>
      </div>

      {linked.length > 0 && (
        <>
          <div style={{ ...label, marginBottom: 10 }}>Linked units</div>
          <div style={{ border: "1px solid var(--rule2)", marginBottom: 24 }}>
            {linked.map((u) => (
              <div key={u.id} style={{ padding: "12px 14px", borderBottom: "1px solid var(--rule)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ font: "600 13px/1.25 var(--bq-font-display)", color: "var(--ink)" }}>{u.addr || u.id}</div>
                <div style={{ ...note, textAlign: "right" }}>
                  {[u.beds != null && `${u.beds} bd`, u.baths != null && `${u.baths} ba`].filter(Boolean).join(" · ") || ""}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── Resident tickets ── */}
      <div style={{ ...label, marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
        Requests
        {resTickets && resTickets.length > 0 && (
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 16, height: 16, padding: "0 4px", borderRadius: 8, background: "rgba(0,0,0,0.12)", font: "700 9px/1 var(--bq-font-display)", color: "var(--ink)" }}>
            {resTickets.length}
          </span>
        )}
      </div>
      {resTicketsErr ? (
        <div style={{ font: "400 12px/1.5 var(--bq-font-body)", color: "var(--muted2)" }}>Could not load tickets: {resTicketsErr}</div>
      ) : !resTickets ? (
        <div style={{ font: "400 12px/1.5 var(--bq-font-body)", color: "var(--muted2)" }}>Loading…</div>
      ) : !resident.email ? (
        <div style={{ font: "400 12px/1.5 var(--bq-font-body)", color: "var(--muted2)" }}>No email on file — tickets are matched by email.</div>
      ) : resTickets.length === 0 ? (
        <div style={{ font: "400 12px/1.5 var(--bq-font-body)", color: "var(--muted2)" }}>No requests on record.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {resTickets.map((t) => <TicketRow key={t.id} ticket={t} />)}
        </div>
      )}
    </div>
  );
}

// ── Residents list ────────────────────────────────────────────────────────────
function ResidentsList({ residents, error, onSelect, onRetry }) {
  if (error) {
    return (
      <div style={{ padding: "28px 0", display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ font: "600 12px/1.4 var(--bq-font-display)", color: "var(--ink)", textTransform: "uppercase", letterSpacing: ".08em" }}>Could not load residents</div>
        <div style={{ font: "400 12px/1.5 var(--bq-font-body)", color: "var(--muted2)" }}>{error}</div>
        {onRetry && <button onClick={onRetry} style={{ alignSelf: "flex-start", border: "1px solid var(--rule2)", background: "transparent", padding: "7px 13px", font: "600 10px/1 var(--bq-font-display)", letterSpacing: ".1em", textTransform: "uppercase", cursor: "pointer", color: "var(--ink)" }}>Retry</button>}
      </div>
    );
  }
  if (!residents) {
    return <div style={{ padding: "32px 0", font: "400 13px/1.5 var(--bq-font-body)", color: "var(--muted2)" }}>Loading residents…</div>;
  }
  if (residents.length === 0) {
    return (
      <div style={{ padding: "28px 0", display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ font: "400 13px/1.5 var(--bq-font-body)", color: "var(--muted2)" }}>No residents on record.</div>
        <div style={{ font: "400 11.5px/1.4 var(--bq-font-body)", color: "var(--muted2)" }}>Use <strong>Onboard a community</strong> → step 4 to add residents.</div>
      </div>
    );
  }
  return (
    <div style={{ border: "1px solid var(--rule2)" }}>
      {residents.map((r) => (
        <button
          key={r.id}
          onClick={() => onSelect(r)}
          style={{
            display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left",
            border: "none", borderBottom: "1px solid var(--rule)", background: "transparent",
            padding: "13px 14px", minHeight: 52, cursor: "pointer",
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ font: "600 13px/1.25 var(--bq-font-display)", color: "var(--ink)" }}>{r.name || r.id}</span>
              {r.board && (
                <svg width="11" height="11" viewBox="0 0 24 24" fill="var(--ink)" stroke="none">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
              )}
            </div>
            <div style={{ ...note, marginTop: 3 }}>
              {[r.rel, r.board && (r.role || "Board member"), r.email].filter(Boolean).join(" · ")}
            </div>
          </div>
          <ChevronRight />
        </button>
      ))}
    </div>
  );
}

// ── Unit detail panel ─────────────────────────────────────────────────────────
function UnitDetail({ unit, residents, onBack }) {
  const linked = (residents || []).filter((r) => (r.units || []).includes(unit.id));
  return (
    <div>
      <DetailBreadcrumb label="Back to units" onBack={onBack} />
      <div style={{ font: "800 clamp(18px, 2.6vw, 22px)/1.08 var(--bq-font-display)", letterSpacing: "-0.02em", marginBottom: 18 }}>{unit.addr || unit.id}</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 1, background: "var(--rule)", border: "1px solid var(--rule2)", marginBottom: 20 }}>
        {[
          { k: "Bedrooms", v: unit.beds != null ? unit.beds : "—" },
          { k: "Bathrooms", v: unit.baths != null ? unit.baths : "—" },
          { k: "Patio", v: unit.patio ? "Yes" : "No" },
          { k: "Garage", v: unit.garage ? "Yes" : "No" },
        ].map(({ k, v }) => (
          <div key={k} style={{ background: "var(--bg)", padding: "14px 16px 16px", display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={label}>{k}</div>
            <div style={{ font: "600 13px/1.3 var(--bq-font-display)", color: "var(--ink)" }}>{String(v)}</div>
          </div>
        ))}
      </div>
      {unit.plates && unit.plates.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ ...label, marginBottom: 8 }}>License plates</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {unit.plates.map((p) => (
              <span key={p} style={{ border: "1px solid var(--rule2)", padding: "5px 10px", font: "600 12px/1 var(--bq-font-display)", letterSpacing: ".08em" }}>{p}</span>
            ))}
          </div>
        </div>
      )}
      {linked.length > 0 && (
        <>
          <div style={{ ...label, marginBottom: 10 }}>Residents</div>
          <div style={{ border: "1px solid var(--rule2)" }}>
            {linked.map((r) => (
              <div key={r.id} style={{ padding: "12px 14px", borderBottom: "1px solid var(--rule)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ font: "600 13px/1.25 var(--bq-font-display)", color: "var(--ink)" }}>{r.name || r.id}</div>
                <div style={{ ...note }}>{r.rel}{r.board ? ` · ${r.role || "Board"}` : ""}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Units list ────────────────────────────────────────────────────────────────
function UnitsList({ units, error, onSelect, onRetry }) {
  if (error) {
    return (
      <div style={{ padding: "28px 0", display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ font: "600 12px/1.4 var(--bq-font-display)", color: "var(--ink)", textTransform: "uppercase", letterSpacing: ".08em" }}>Could not load units</div>
        <div style={{ font: "400 12px/1.5 var(--bq-font-body)", color: "var(--muted2)" }}>{error}</div>
        {onRetry && <button onClick={onRetry} style={{ alignSelf: "flex-start", border: "1px solid var(--rule2)", background: "transparent", padding: "7px 13px", font: "600 10px/1 var(--bq-font-display)", letterSpacing: ".1em", textTransform: "uppercase", cursor: "pointer", color: "var(--ink)" }}>Retry</button>}
      </div>
    );
  }
  if (!units) {
    return <div style={{ padding: "32px 0", font: "400 13px/1.5 var(--bq-font-body)", color: "var(--muted2)" }}>Loading units…</div>;
  }
  if (units.length === 0) {
    return (
      <div style={{ padding: "28px 0", display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ font: "400 13px/1.5 var(--bq-font-body)", color: "var(--muted2)" }}>No units on record.</div>
        <div style={{ font: "400 11.5px/1.4 var(--bq-font-body)", color: "var(--muted2)" }}>Use <strong>Onboard a community</strong> → step 3 to add units.</div>
      </div>
    );
  }
  return (
    <div style={{ border: "1px solid var(--rule2)" }}>
      {units.map((u) => (
        <button
          key={u.id}
          onClick={() => onSelect(u)}
          style={{
            display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left",
            border: "none", borderBottom: "1px solid var(--rule)", background: "transparent",
            padding: "13px 14px", minHeight: 52, cursor: "pointer",
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ font: "600 13px/1.25 var(--bq-font-display)", color: "var(--ink)" }}>{u.addr || u.id}</div>
            <div style={{ ...note, marginTop: 3 }}>
              {[u.beds != null && `${u.beds} bd`, u.baths != null && `${u.baths} ba`, u.garage && "Garage", u.patio && "Patio"].filter(Boolean).join(" · ") || "No details"}
            </div>
          </div>
          <ChevronRight />
        </button>
      ))}
    </div>
  );
}

// ── Requests tab ─────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const palette = { open: "#d97706", resolved: "#16a34a", closed: "#6b7280" };
  const color = palette[status] || "var(--muted2)";
  return (
    <span style={{
      flexShrink: 0, display: "inline-block", padding: "2px 8px", borderRadius: 10,
      font: "700 9px/1.5 var(--bq-font-display)", letterSpacing: ".08em", textTransform: "uppercase",
      background: color + "22", color,
    }}>{status || "—"}</span>
  );
}

function TicketRow({ ticket }) {
  const [open, setOpen] = useState(false);
  const date = ticket.created_at
    ? new Date(ticket.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : "";
  const summary = ticket.summary || ticket.original_message?.slice(0, 100) || "(no summary)";
  return (
    <div style={{ borderBottom: "1px solid var(--rule)", padding: "14px 0" }}>
      <div
        onClick={() => setOpen((o) => !o)}
        style={{ display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer" }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ font: "600 13px/1.4 var(--bq-font-body)", color: "var(--ink)" }}>{summary}</div>
          <div style={{ font: "400 11px/1.4 var(--bq-font-body)", color: "var(--muted2)", marginTop: 3, display: "flex", flexWrap: "wrap", gap: "3px 10px" }}>
            {ticket.category && <span>{ticket.category}</span>}
            {ticket.requester?.identifier && <span>{ticket.requester.identifier}</span>}
            {date && <span>{date}</span>}
          </div>
        </div>
        <StatusBadge status={ticket.status} />
      </div>
      {open && (
        <div style={{ marginTop: 10, padding: "12px 14px", background: "rgba(0,0,0,0.04)", borderRadius: 6, font: "400 12px/1.6 var(--bq-font-body)", color: "var(--ink)", whiteSpace: "pre-wrap" }}>
          {ticket.agent_answer || ticket.original_message || "(no detail)"}
        </div>
      )}
    </div>
  );
}

function RequestsTab({ tickets, error, onRetry }) {
  if (error) {
    return (
      <div style={{ padding: "28px 0", display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ font: "600 12px/1.4 var(--bq-font-display)", color: "var(--ink)", textTransform: "uppercase", letterSpacing: ".08em" }}>Could not load tickets</div>
        <div style={{ font: "400 12px/1.5 var(--bq-font-body)", color: "var(--muted2)" }}>{error}</div>
        {onRetry && <button onClick={onRetry} style={{ alignSelf: "flex-start", border: "1px solid var(--rule2)", background: "transparent", padding: "7px 13px", font: "600 10px/1 var(--bq-font-display)", letterSpacing: ".1em", textTransform: "uppercase", cursor: "pointer", color: "var(--ink)" }}>Retry</button>}
      </div>
    );
  }
  if (!tickets) {
    return <div style={{ padding: "32px 0", font: "400 13px/1.5 var(--bq-font-body)", color: "var(--muted2)" }}>Loading requests…</div>;
  }
  if (tickets.length === 0) {
    return <div style={{ padding: "32px 0", font: "400 13px/1.5 var(--bq-font-body)", color: "var(--muted2)" }}>No open requests.</div>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {tickets.map((t) => <TicketRow key={t.id} ticket={t} />)}
    </div>
  );
}

// ── Tab strip ─────────────────────────────────────────────────────────────────
const TABS = ["residents", "units", "requests"];

function TabStrip({ active, onChange, community }) {
  return (
    <div style={{ display: "flex", justifyContent: "center", borderBottom: "2px solid var(--ink)", marginBottom: 20 }}>
      {TABS.map((tab) => {
        const isActive = tab === active;
        const badge = tab === "requests" && community?.requests > 0 ? community.requests : null;
        return (
          <button
            key={tab}
            onClick={() => onChange(tab)}
            style={{
              border: "none", background: "none", cursor: "pointer",
              padding: "12px 22px",
              font: `${isActive ? 700 : 600} 11px/1 var(--bq-font-display)`,
              letterSpacing: ".12em", textTransform: "uppercase",
              color: isActive ? "var(--ink)" : "var(--muted2)",
              borderBottom: isActive ? "2px solid var(--accent)" : "2px solid transparent",
              marginBottom: -2,
              display: "flex", alignItems: "center", gap: 6,
            }}
          >
            {tab}
            {badge !== null && (
              <span style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                minWidth: 16, height: 16, padding: "0 4px", borderRadius: 8,
                background: isActive ? "var(--accent)" : "rgba(0,0,0,0.12)",
                color: isActive ? "#fff" : "var(--ink)",
                font: "700 9px/1 var(--bq-font-display)", letterSpacing: 0,
              }}>{badge}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** Full-page community detail, reached at #/admin/communities/{id}. Replaces
 * the dashboard body entirely (rather than expanding inline under the list)
 * so a community has a real, linkable/refreshable page of its own -- see
 * AdminApp.jsx's hash routing. */
export function CommunityDetailPage({ community, cards, loading, onBack }) {
  const [activeTab, setActiveTab] = useState("residents");
  const [residents, setResidents] = useState(null);
  const [units, setUnits] = useState(null);
  const [dirError, setDirError] = useState(null);
  const [selectedResident, setSelectedResident] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [tickets, setTickets] = useState(null);
  const [ticketsError, setTicketsError] = useState(null);
  const [ticketsLoaded, setTicketsLoaded] = useState(false);

  const loadDirectory = (communityId) => {
    setResidents(null);
    setUnits(null);
    setDirError(null);
    setSelectedResident(null);
    setSelectedUnit(null);
    let cancelled = false;
    Promise.all([
      api.listResidents(communityId),
      api.listUnits(communityId),
    ]).then(([rBody, uBody]) => {
      if (cancelled) return;
      setResidents(rBody.residents || []);
      setUnits(uBody.units || []);
    }).catch((err) => {
      if (cancelled) return;
      const msg = err?.message || String(err) || "Unknown error";
      setDirError(msg);
      setResidents([]);
      setUnits([]);
    });
    return () => { cancelled = true; };
  };

  const loadTickets = (communityId) => {
    setTickets(null);
    setTicketsError(null);
    let cancelled = false;
    api.listAdminTickets(communityId, { status: "open" })
      .then((body) => {
        if (cancelled) return;
        setTickets(body.tickets || []);
        setTicketsLoaded(true);
      })
      .catch((err) => {
        if (cancelled) return;
        setTicketsError(err?.message || String(err) || "Unknown error");
        setTickets([]);
        setTicketsLoaded(true);
      });
    return () => { cancelled = true; };
  };

  useEffect(() => {
    if (!community?.id) return;
    setTicketsLoaded(false);
    setTickets(null);
    setTicketsError(null);
    return loadDirectory(community.id);
  }, [community?.id]);

  // Lazy-load tickets the first time the requests tab is opened for this community
  useEffect(() => {
    if (activeTab !== "requests" || ticketsLoaded || !community?.id) return;
    return loadTickets(community.id);
  }, [activeTab, ticketsLoaded, community?.id]);

  const switchTab = (tab) => {
    setActiveTab(tab);
    setSelectedResident(null);
    setSelectedUnit(null);
  };

  return (
    <div>
      <button
        onClick={onBack}
        style={{
          display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", cursor: "pointer",
          padding: 0, marginBottom: 18,
          font: "600 10px/1 var(--bq-font-display)", letterSpacing: ".1em", textTransform: "uppercase", color: "var(--muted2)",
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
        </svg>
        Back to communities
      </button>

      {!community ? (
        <div style={{ padding: "40px 0", font: "400 13px/1.5 var(--bq-font-body)", color: "var(--muted2)" }}>
          {loading ? "Loading community…" : "That community couldn't be found."}
        </div>
      ) : (
        <>
          <div style={{ borderBottom: "2px solid var(--ink)", paddingBottom: 16, marginBottom: 20 }}>
            <div style={{ font: "800 clamp(26px, 4vw, 38px)/1.05 var(--bq-font-display)", letterSpacing: "-0.03em" }}>{community.name}</div>
            <div style={{ font: "400 13px/1.4 var(--bq-font-display)", color: "var(--muted2)", marginTop: 7 }}>
              {placeOf(community)} · {community.status}
            </div>
          </div>
          <DetailCardsGrid cards={cards} />

          {/* ── Board members dashboard section ── */}
          {residents && residents.filter((r) => r.board).length > 0 && (
            <div style={{ marginTop: 24 }}>
              <div style={{ ...label, marginBottom: 10 }}>Board</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {residents.filter((r) => r.board).map((r) => (
                  <div key={r.id} style={{ border: "1px solid var(--rule2)", padding: "10px 14px", display: "flex", alignItems: "center", gap: 9, background: "var(--surface)" }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="var(--ink)" stroke="none">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                    <div>
                      <div style={{ font: "600 12px/1.2 var(--bq-font-display)", color: "var(--ink)" }}>{r.name || r.email}</div>
                      <div style={{ font: "400 10.5px/1.2 var(--bq-font-body)", color: "var(--muted2)", marginTop: 2 }}>{r.role || "Member at large"}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Directory tabs ── */}
          <div style={{ marginTop: 32 }}>
            <TabStrip active={activeTab} onChange={switchTab} community={community} />

            {activeTab === "residents" && (
              selectedResident
                ? <ResidentDetail
                    resident={selectedResident}
                    units={units}
                    communityId={community.id}
                    onBack={() => setSelectedResident(null)}
                    onResidentUpdated={(updated) => {
                      setResidents((prev) => (prev || []).map((r) => r.id === updated.id ? updated : r));
                      setSelectedResident(updated);
                    }}
                  />
                : <ResidentsList residents={dirError ? [] : residents} error={dirError} onSelect={setSelectedResident} onRetry={() => loadDirectory(community.id)} />
            )}

            {activeTab === "units" && (
              selectedUnit
                ? <UnitDetail unit={selectedUnit} residents={residents} onBack={() => setSelectedUnit(null)} />
                : <UnitsList units={dirError ? [] : units} error={dirError} onSelect={setSelectedUnit} onRetry={() => loadDirectory(community.id)} />
            )}

            {activeTab === "requests" && (
              <RequestsTab
                tickets={ticketsError ? [] : tickets}
                error={ticketsError}
                onRetry={() => { setTicketsLoaded(false); loadTickets(community.id); }}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}

function FileCard({ file }) {
  const done = file.status === "indexed";
  return (
    <div style={{ border: "1px solid var(--ink)", background: "var(--surface)", padding: "12px 13px", display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ flex: 1, minWidth: 0, font: "600 13px/1.2 var(--bq-font-display)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</span>
        <Badge tone={done ? "solid" : "outline"} style={done ? undefined : { animation: "bq-pulse 1.2s ease-in-out infinite" }}>
          {done && (
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--bg)" strokeWidth="3.5"><path d="M20 6L9 17l-5-5" /></svg>
          )}
          {file.statusLabel}
        </Badge>
      </div>
      <Progress pct={file.pct} />
      <div style={{ font: "400 10.5px/1 var(--bq-font-display)", letterSpacing: ".06em", textTransform: "uppercase", color: "var(--muted2)" }}>
        {file.label} · {file.sections} §
      </div>
    </div>
  );
}

function DropZone({ onPick, accept, title, hint, required = false }) {
  const input = useRef(null);
  return (
    <>
      <input
        ref={input}
        type="file"
        accept={accept}
        onChange={(e) => e.target.files?.[0] && onPick(e.target.files[0])}
        style={{ display: "none" }}
      />
      <button
        onClick={() => input.current?.click()}
        style={{
          width: "100%",
          border: `1px dashed ${required ? "var(--accent)" : "var(--rule2)"}`,
          background: "transparent",
          padding: "24px 16px",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
          cursor: "pointer", minHeight: 116,
        }}
      >
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M17 8l-5-5-5 5" /><path d="M12 3v12" />
        </svg>
        <span style={{ font: "700 13.5px/1.2 var(--bq-font-display)", color: "var(--ink)" }}>{title}</span>
        <span style={{ font: "400 11.5px/1.2 var(--bq-font-body)", color: "var(--muted2)" }}>{hint}</span>
      </button>
    </>
  );
}

export function OnboardWizard({ admin }) {
  const { wizard, patchWizard } = admin;
  if (!admin.wizardOpen) return null;

  return (
    <div style={{ position: "absolute", inset: 0, zIndex: 120, background: "var(--scrim)", display: "flex", flexDirection: "column", alignItems: "center", padding: "clamp(10px, 2vw, 28px)" }}>
      <div style={{ flex: 1, minHeight: 0, width: "100%", maxWidth: 720, background: "var(--bg)", border: "2px solid var(--ink)", display: "flex", flexDirection: "column", animation: "bq-rise .22s cubic-bezier(.2,.7,.3,1) both" }}>

        <div style={{ flex: "none", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, padding: "14px 20px 12px" }}>
          <div>
            <div style={{ ...label, color: "var(--accentDeep)", marginBottom: 5 }}>Association onboarding</div>
            <div style={{ font: "800 clamp(18px, 2.6vw, 22px)/1 var(--bq-font-display)", letterSpacing: "-0.02em" }}>Onboard a community</div>
          </div>
          <button onClick={admin.closeWizard} aria-label="Close onboarding" style={{ border: "1px solid var(--ink)", background: "transparent", width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flex: "none" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--ink)" strokeWidth="3"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
        </div>

        <div style={{ flex: "none", display: "grid", gridTemplateColumns: "repeat(3, 1fr)", borderTop: "1px solid var(--ink)", borderBottom: "1px solid var(--ink)" }}>
          {WIZARD_STEPS.map((step, i) => {
            const active = wizard.step === i;
            return (
              <button
                key={step}
                onClick={() => admin.setStep(i)}
                style={{
                  minWidth: 0, border: "none", borderRight: "1px solid var(--rule)",
                  background: active ? "var(--ink)" : "transparent",
                  color: active ? "var(--bg)" : "var(--muted2)",
                  display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 5,
                  padding: "10px 11px 12px", cursor: "pointer", minHeight: 52,
                }}
              >
                <span style={{ font: "600 9px/1 var(--bq-font-display)", letterSpacing: ".1em", color: active ? "var(--accentSoft)" : "var(--rule2)" }}>0{i + 1}</span>
                <span style={{ font: "700 12px/1.1 var(--bq-font-display)", letterSpacing: ".03em", textTransform: "uppercase", textAlign: "left" }}>{step}</span>
              </button>
            );
          })}
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "16px 20px 20px", display: "flex", flexDirection: "column", gap: 18 }}>

          {wizard.step === 0 && (
            <>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div>
                  <div style={{ font: "800 17px/1.1 var(--bq-font-display)", letterSpacing: "-0.01em", marginBottom: 5 }}>Community details</div>
                  <p style={{ margin: 0, font: "400 13px/1.5 var(--bq-font-body)", color: "var(--body)", textWrap: "pretty" }}>
                    The name appears in every answer the assistant gives. The mailing address, city, state and ZIP ground answers about local ordinances.
                  </p>
                </div>
                <Field label="Community name">
                  <input value={wizard.name} onChange={(e) => patchWizard({ name: e.target.value })} placeholder="Oakridge Estates" style={{ ...inputStyle, font: "600 15px/1.2 var(--bq-font-display)" }} />
                </Field>
                <Field label="Street address">
                  <input value={wizard.street} onChange={(e) => patchWizard({ street: e.target.value })} placeholder="1420 Oakridge Way" style={inputStyle} />
                </Field>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <Field label="City" style={{ flex: 1, minWidth: 150 }}>
                    <input value={wizard.city} onChange={(e) => patchWizard({ city: e.target.value })} placeholder="Milpitas" style={inputStyle} />
                  </Field>
                  <Field label="State" style={{ width: 96, flex: "none" }}>
                    <input value={wizard.state} maxLength={2} onChange={(e) => patchWizard({ state: e.target.value })} placeholder="CA" style={{ ...inputStyle, textTransform: "uppercase" }} />
                  </Field>
                  <Field label="ZIP code" style={{ width: 116, flex: "none" }}>
                    <input value={wizard.zip} onChange={(e) => patchWizard({ zip: e.target.value })} placeholder="95035" style={inputStyle} />
                  </Field>
                  <Field label="Units" style={{ width: 110, flex: "none" }}>
                    <input value={wizard.units} onChange={(e) => patchWizard({ units: e.target.value })} placeholder="148" style={inputStyle} />
                  </Field>
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <span style={{ font: "800 15px/1.1 var(--bq-font-display)" }}>Property addresses</span>
                  <span style={{ font: "400 10px/1 var(--bq-font-display)", color: "var(--muted2)", textTransform: "uppercase", letterSpacing: ".1em" }}>Optional</span>
                </div>
                {wizard.addressFile ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid var(--ink)", background: "var(--surface)", padding: "12px 13px" }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="3" style={{ flex: "none" }}><path d="M20 6L9 17l-5-5" /></svg>
                    <span style={{ flex: 1, minWidth: 0, font: "600 13px/1.2 var(--bq-font-display)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{wizard.addressFile}</span>
                    <span style={{ font: "400 11px/1 var(--bq-font-display)", color: "var(--muted2)", flex: "none" }}>{wizard.addressRows} rows</span>
                  </div>
                ) : (
                  <DropZone onPick={admin.uploadAddresses} accept=".csv,.txt" title="Drop your address list here" hint="or click to browse · .csv or .txt" />
                )}
                <p style={{ margin: 0, font: "400 11.5px/1.45 var(--bq-font-body)", color: "var(--muted2)" }}>
                  CSV (first column used as the address) or plain text, one per line. Rows post to the association's server.
                </p>
              </div>
            </>
          )}

          {wizard.step === 1 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <div style={{ font: "800 17px/1.1 var(--bq-font-display)", letterSpacing: "-0.01em", marginBottom: 5 }}>CC&amp;Rs</div>
                <p style={{ margin: 0, font: "400 13px/1.5 var(--bq-font-body)", color: "var(--body)", textWrap: "pretty" }}>
                  Required. The declaration is the spine of the corpus — every answer is read against it before the statute.
                </p>
              </div>
              {admin.ccrFiles.length === 0 ? (
                <DropZone required onPick={(file) => admin.uploadDocument("CC&Rs", file, { sections: 148 })} accept=".pdf,.docx,.md" title="Drop the CC&Rs here" hint="or click to browse · .pdf, .docx or .md" />
              ) : (
                admin.ccrFiles.map((f) => <FileCard key={f.id} file={f} />)
              )}
            </div>
          )}

          {wizard.step === 2 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <div style={{ font: "800 17px/1.1 var(--bq-font-display)", letterSpacing: "-0.01em", marginBottom: 5 }}>Supporting documents</div>
                <p style={{ margin: 0, font: "400 13px/1.5 var(--bq-font-body)", color: "var(--body)", textWrap: "pretty" }}>
                  Bylaws and operating rules. Each is chunked, embedded and cited separately — add them now or any time later.
                </p>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Button variant="secondary" onClick={() => admin.uploadDocument("Bylaws", null, { name: "bylaws.pdf", sections: 63 })} style={{ flex: 1, minWidth: 150, flexDirection: "column", alignItems: "flex-start", gap: 5, minHeight: 62, padding: "12px 10px" }}>
                  <span>Bylaws</span>
                  <span style={{ font: "400 10.5px/1.2 var(--bq-font-body)", color: "var(--muted2)", textTransform: "none", letterSpacing: 0 }}>Upload · 63 §</span>
                </Button>
                <Button variant="secondary" onClick={() => admin.uploadDocument("Rules & Regs", null, { name: "rules-2026.pdf", sections: 46 })} style={{ flex: 1, minWidth: 150, flexDirection: "column", alignItems: "flex-start", gap: 5, minHeight: 62, padding: "12px 10px" }}>
                  <span>Rules &amp; Regs</span>
                  <span style={{ font: "400 10.5px/1.2 var(--bq-font-body)", color: "var(--muted2)", textTransform: "none", letterSpacing: 0 }}>Upload · 46 §</span>
                </Button>
              </div>
              {admin.otherFiles.map((f) => <FileCard key={f.id} file={f} />)}
              <div style={{ border: "1px solid var(--rule2)", background: "var(--surface)", padding: "12px 13px", display: "flex", flexDirection: "column", gap: 5 }}>
                <div style={label}>Corpus summary</div>
                <div style={{ font: "700 13px/1.35 var(--bq-font-display)", color: "var(--ink)" }}>{admin.corpusSummary}</div>
              </div>
            </div>
          )}

          {wizard.step === 3 && <UnitsStep dir={admin.dir} />}

          {wizard.step === 4 && <ResidentsStep dir={admin.dir} />}

          {admin.log.length > 0 && (
            <Console title="Backend console" endpoint={admin.endpoint} lines={admin.log} open={admin.logOpen} onToggle={admin.toggleLog} maxHeight={168} />
          )}
        </div>

        <div style={{ flex: "none", borderTop: "1px solid var(--ink)", background: "var(--surface)", padding: "12px 20px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          {wizard.step === 0 ? (
            <span style={{ font: "600 10px/1 var(--bq-font-display)", letterSpacing: ".1em", textTransform: "uppercase", color: "var(--muted2)" }}>Step 1 of {WIZARD_STEPS.length}</span>
          ) : (
            <Button variant="ghost" onClick={admin.back}>Back</Button>
          )}
          {wizard.step === WIZARD_STEPS.length - 1 ? (
            <Button onClick={admin.finishWizard} style={{ flex: "none" }}>Finish setup</Button>
          ) : (
            <Button onClick={admin.next} disabled={!admin.canAdvance} style={{ flex: "none" }}>{admin.nextLabel}</Button>
          )}
        </div>
      </div>
    </div>
  );
}
